import CustomStore from 'devextreme/data/custom_store';
import stringify from '../utils/stableStringify';
import AntAPI from '../utils/AntAPI';

export default class AntQueryStore {
    constructor(args) {
        this.initConditions = this.initConditions.bind(this);
        this.callAPI = this.callAPI.bind(this);
        this.loadDataSource = this.loadDataSource.bind(this);
        this.refresh = this.refresh.bind(this);
        this.resetCurrentData = this.resetCurrentData.bind(this);
        this.getCurrentData = this.getCurrentData.bind(this);
        this.getCurrentItem = this.getCurrentItem.bind(this);
        this.getItem = this.getItem.bind(this);
        this.isQuerried = this.isQuerried.bind(this);

        this.currentData = { data: [], conditions: [] };
        this.querried = false;

        this.reInit(args);
    }

    reInit(args) {
        this.config = args;
        this.getSearchConditions = args.getSearchConditions;
        this.getParentSearchConditions = args.getParentSearchConditions;
        this.getFilteredGroupConditions = args.getFilteredGroupConditions;
        this.getOrderings = args.getOrderings;
        this.getQueryFields = args.getQueryFields;
        this.getSearchText = args.getSearchText;
        this.getOrderBy = args.getOrderBy;
        this.getOrderType = args.getOrderType;
        this.isAllowQuery = args.isAllowQuery;
        this.onGotData = args.onGotData;
        this.onLoaded = args.onLoaded;
        this.onLoading = args.onLoading;
        this.onFiltering = args.onFiltering;

        this.loadFunc = args.loadFunc || 'getList';
        this.API = args.API;
        this.url = args.url;
        this.pageSize = args.pageSize || 20;

        this.options = {
            key: args.key || 'id',
            load: this.loadDataSource,
            byKey: this.getItem
        };

        if (!this.dataSource) {
            this.dataSource = new CustomStore(this.options);
        } else if (args.forceReload) {
            this.dataSource.load(this.options);
        }
    }

    instance() {
        return this.dataSource;
    }

    getItem(keyValue) {
        const keyName = this.options.key;
        return Array.isArray(this.currentData.data) ? this.currentData.data.find(c => c?.[keyName] === keyValue) : undefined;
    }

    getCurrentData() {
        return this.currentData.data;
    }

    getCurrentItem(keyValue) {
        const keyName = this.options.key;
        const ar = this.getCurrentData();
        return Array.isArray(ar) ? ar.find(c => c?.[keyName] === keyValue) : null;
    }

    async refresh() {
        await this.dataSource.load();
    }

    async resetCurrentData(obj) {
        if (!obj || !Array.isArray(obj.data)) {
            this.currentData = { data: [], conditions: [] };
        } else {
            this.currentData = obj;
            this.onLoaded?.(this.currentData);
        }
    }

    _isValEmpty(v) {
        if (v == null || v === '' || v === 0) return true;
        if (Array.isArray(v)) return v.length === 0;
        if (typeof v === 'object') return Object.keys(v).length === 0;
        return false;
    }

    isEmptyColumn(colName) {
        const rows = this.currentData?.data;
        if (this.querried && Array.isArray(rows) && rows.length > 0) {
            return !rows.some(d => !this._isValEmpty(d?.[colName]));
        }
        return false;
    }

    isEmptyColumns(colNames) {
        const rows = this.currentData?.data;
        if (this.querried && Array.isArray(rows) && rows.length > 0) {
            return colNames.every(colName => !rows.some(d => !this._isValEmpty(d?.[colName])));
        }
        return false;
    }

    async isQuerried() {
        return Boolean(this.querried);
    }

    async loadDataSource(opts) {
        this.currentOpts = opts;
        const res = await this.callAPI(opts);
        return {
            data: res?.data || [],
            totalCount: res?.totalCount ?? (res?.data?.length || 0)
        };
    }

    async initConditions(opts) {
        const take = parseInt(opts.take);
        if (!isNaN(take) && take > 0) this.pageSize = take;

        if (typeof this.getSearchText === 'function') this.searchText = this.getSearchText(opts);

        const skip = parseInt(opts.skip) || 0;
        this.pageIndex = Math.floor(skip / (this.pageSize || 20));

        if (typeof this.getOrderBy === 'function') this.orderBy = this.getOrderBy(opts);
        if (typeof this.getOrderType === 'function') this.orderType = this.getOrderType(opts);

        const condsObj = {};
        const helper = (fn) => {
            if (typeof fn === 'function') {
                const obj = fn();
                if (obj && typeof obj === 'object') {
                    Object.entries(obj).forEach(([key, val]) => {
                        if (val !== undefined) condsObj[key] = val;
                    });
                }
            }
        };

        helper(this.getSearchConditions);
        helper(this.getParentSearchConditions);
        helper(this.getFilteredGroupConditions);

        const conditions = Object.keys(condsObj).map(key => ({
            name: key,
            value: condsObj[key]
        }));

        const sText = String(this.searchText || '').trim();
        if (sText !== '') {
            conditions.push({ name: 'searchText', value: sText });
        }

        const addArrayCond = (fn, name) => {
            if (typeof fn === 'function') {
                const obj = fn();
                if (Array.isArray(obj) && obj.length > 0) {
                    conditions.push({ name, value: obj });
                }
            }
        };

        addArrayCond(this.getOrderings, 'ordering');
        addArrayCond(this.getQueryFields, 'selectFields');

        if (this.orderBy != null && String(this.orderBy).trim() !== '') {
            conditions.push({ name: 'orderBy', value: this.orderBy });
        }
        if (this.orderType != null && String(this.orderType).trim() !== '') {
            conditions.push({ name: 'orderType', value: this.orderType });
        }

        conditions.push({ name: 'pageSize', value: this.pageSize });
        conditions.push({ name: 'pageIndex', value: this.pageIndex });

        return conditions;
    }

    async callAPI(opts) {
        if (!this.API) return { data: [], totalCount: 0 };

        const conditions = await this.initConditions(opts);
        this.onLoading?.(conditions);

        let results;
        const apiOptions = { byPassTotal: !this.config?.remotePaging };

        try {
            if (this.url) {
                results = await this.API.getList(conditions, this.url, { ...apiOptions, byPassDefaultConds: true });
            } else {
                const method = this.loadFunc || 'getList';
                const callFunc = this.API[method] || AntAPI.prototype[method];
                results = await callFunc.call(this.API, conditions, null, apiOptions);
            }
        } catch (error) {
            console.error('AntQueryStore API Error:', error);
            results = { rows: [], total: 0 };
        }

        if (!results) results = { rows: [], total: 0 };

        const data = {
            summary: results.summary,
            data: results.rows || (Array.isArray(results) ? results : []),
            totalCount: results.total ?? (Array.isArray(results) ? results.length : (results.rows?.length || 0)),
            conditions: conditions
        };

        this.querried = true;
        await this.resetCurrentData(data);

        return data;
    }
}
